import { type ReactNode } from 'react';
import { graphql, readInlineData } from 'react-relay';

import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage, type IntlShape } from 'dibs-react-intl';

import { CurrencyProvider } from '../../CurrencyProvider/CurrencyProvider';
import ExpiringPromoMessage from '../ExpiringPromoMessage';
import { shouldShowExpiringPromo } from './expiringPromoMessageHelpers';
import {
    messageItemTemplate,
    isLowResolution,
    getNoteByDevice,
    hasAnyText,
    globalFallbackMessage,
    type MessageItem,
    type MessageItemTemplateData,
    type NoteObj,
} from './sharedMessageHelpers';

import { type messageHelpers_user$key } from './__generated__/messageHelpers_user.graphql';
import { type messageHelpers_promotion$key } from './__generated__/messageHelpers_promotion.graphql';
import { type messageHelpers_expiringPromotion$key } from './__generated__/messageHelpers_expiringPromotion.graphql';

type ImpressionCapsType = { [key: string]: number };

const LAST_BANNER_MESSAGE_ID = 'lastBannerMessageId';
const MESSAGE_IMPRESSION_CAPS = 'messageImpressionCaps';
const TERMS_URL = '/promotions/terms/?promo=';

const shownIds: { [key: string]: boolean } = {};

const userFragment = graphql`
    fragment messageHelpers_user on User @inline {
        preferences {
            currency
        }
    }
`;

const promotionFragment = graphql`
    fragment messageHelpers_promotion on Promotion @inline {
        serviceId
        buyerDisplayable
        flatAdjustmentRate {
            rate {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        code
        promotionMeta {
            customBannerMessage
            desktopBannerMessage
            desktopBannerCta
            mobileBannerMessage
            mobileBannerCta
        }
    }
`;

const expiringPromotionFragment = graphql`
    fragment messageHelpers_expiringPromotion on Promotion @inline {
        ...ExpiringPromoMessage_promotion
        ...expiringPromoMessageHelpers_expiringPromotion
    }
`;

const getNoteId = (messageItem: MessageItem): string | null | undefined =>
    getNoteByDevice(messageItem)?.serviceId;

export const addToShownList = (messageItem: MessageItem): void => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        shownIds[noteId] = true;
    }
};

export const wasShown = (messageItem: MessageItem): boolean =>
    !!shownIds[getNoteId(messageItem) || ''];

const increaseImpressionCap = (noteId: string): void => {
    const msgImpressCaps =
        (localStorage.getItem(MESSAGE_IMPRESSION_CAPS) as ImpressionCapsType) || {};

    msgImpressCaps[noteId] = msgImpressCaps[noteId] ? msgImpressCaps[noteId] + 1 : 1;

    localStorage.setItem(MESSAGE_IMPRESSION_CAPS, msgImpressCaps);
};

const setNoteIdToStorage = (noteId: string): void => {
    localStorage.setItem(LAST_BANNER_MESSAGE_ID, noteId);
};

export const setValuesToStorage = (messageItem: MessageItem): void => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        increaseImpressionCap(noteId);
        setNoteIdToStorage(noteId);
    }
};

export const getIsFirstTimeVisitor = (): boolean => {
    const { totalVisitCount } =
        (localStorage.getItem('visitCount') as {
            totalVisitCount: number;
        }) || {};
    const { session } =
        (localStorage.getItem('pseudoSessionVisitCount') as {
            session: number;
        }) || {};

    return totalVisitCount <= session;
};

export const getMessageToShow = ({
    intl,
    message,
    useGlobalFallbackMessage,
}: {
    intl: IntlShape;
    message: MessageItem | NoteObj | null;
    useGlobalFallbackMessage: boolean;
}): NoteObj | MessageItem | null => {
    if (!message) {
        return useGlobalFallbackMessage ? globalFallbackMessage(intl) : null;
    }
    const messageToShow = hasAnyText(message) ? message : null;
    if (messageToShow) {
        return messageToShow;
    }
    return useGlobalFallbackMessage ? globalFallbackMessage(intl) : null;
};

export const getPromoMessage = ({
    userRef,
    promotionRef,
    expiringPromotionRef,
    intl,
    defaultCurrency,
}: {
    userRef: messageHelpers_user$key;
    promotionRef: messageHelpers_promotion$key;
    expiringPromotionRef: messageHelpers_expiringPromotion$key;
    intl: IntlShape;
    defaultCurrency: string;
}): MessageItem | NoteObj | null => {
    const userData = readInlineData(userFragment, userRef);
    const activePromotion = readInlineData(promotionFragment, promotionRef) || {};
    const expiringPromotion = readInlineData(expiringPromotionFragment, expiringPromotionRef);
    const { preferences } = userData || {};
    const { promotionMeta, code, flatAdjustmentRate, buyerDisplayable, serviceId } =
        activePromotion;
    const flatRate = flatAdjustmentRate?.rate || { convertedAmountList: [] };
    const {
        customBannerMessage,
        desktopBannerMessage,
        desktopBannerCta,
        mobileBannerMessage,
        mobileBannerCta,
    } = promotionMeta || {};
    if (buyerDisplayable) {
        const url = TERMS_URL + code;

        if (shouldShowExpiringPromo(expiringPromotion)) {
            const content = {
                title: 'expiring promotion',
                message: (
                    <CurrencyProvider
                        render={({ currency }: { currency: string }) => (
                            <ExpiringPromoMessage
                                promotion={expiringPromotion}
                                isLowResolution={isLowResolution}
                                currency={currency}
                            />
                        )}
                    />
                ) as ReactNode,
                cta: {
                    text: (
                        <FormattedMessage
                            id="dbl.Header.MessageBanner.expiringPromoCtaText"
                            defaultMessage="Details"
                        />
                    ) as ReactNode,
                    url,
                },
            } as MessageItemTemplateData;
            const message = messageItemTemplate({
                desktop: content,
                mobile: content,
            });
            return message;
        } else if (desktopBannerMessage || mobileBannerMessage) {
            const updateBannerMessage = (message: string | null | undefined): string | null => {
                if (message && !customBannerMessage && flatAdjustmentRate) {
                    const currency = preferences?.currency || defaultCurrency || 'USD';
                    const amount =
                        (flatRate.convertedAmountList || []).find(c => c?.currency === currency)
                            ?.amount || 0;
                    message = message.replace(
                        /\{discount\}/,
                        intl.formatNumber(amount, {
                            style: 'currency',
                            currency,
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })
                    );
                }
                return message || null;
            };

            const message = {
                note: {
                    serviceId: `${serviceId}_desktop`,
                    content: {
                        title: code,
                        message: updateBannerMessage(desktopBannerMessage),
                        cta: {
                            text: desktopBannerCta || null,
                            url,
                        },
                    },
                    anchors: [
                        {
                            serviceId: `${serviceId}_mobile`,
                            content: {
                                title: code,
                                message: updateBannerMessage(mobileBannerMessage),
                                cta: {
                                    text: mobileBannerCta || null,
                                    url,
                                },
                            },
                        },
                    ],
                },
            };
            return hasAnyText(message) ? message : null;
        }
    }
    return null;
};
